import { useEffect } from 'react';
import DialogComponent from '../common/Dialog';
import { Badge } from '../ui/badge';
import { getStatusColor, capitalizeFirst } from '../../utils';
import { MT5AccountUser } from '../../utils/types';
import LoadingSkeleton from '../common/LoadingSkeleton';

interface AccountDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  account: MT5AccountUser | null;
  title?: string;
  loading?: boolean;
}

export default function AccountDetailsDrawer({
  isOpen,
  onClose,
  account,
  title = 'Account Details',
  loading = false,
}: AccountDetailsDrawerProps) {
  useEffect(() => {
    if (!isOpen) {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const rawStatus = account?.Status ?? (account as any)?.status;
  const rawEmail = account?.Email ?? (account as any)?.email;
  const rawCountry = account?.Country ?? (account as any)?.country;

  const statusVal =
    rawStatus !== undefined && rawStatus !== null
      ? String(rawStatus).trim()
      : '';
  const emailVal =
    rawEmail !== undefined && rawEmail !== null ? String(rawEmail).trim() : '';
  const countryVal =
    rawCountry !== undefined && rawCountry !== null
      ? String(rawCountry).trim()
      : '';

  const hasStatus = statusVal !== '';
  const hasEmail = emailVal !== '';
  const hasCountry = countryVal !== '';

  const hasContactInfo = hasEmail || hasCountry;

  return (
    <DialogComponent
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={account?.Login ? `Login ID: ${account.Login}` : undefined}
      maxWidth="md"
      cancelText="Close"
    >
      {loading ? (
        <div className="p-4">
          <LoadingSkeleton type="detail" />
        </div>
      ) : account ? (
        <div className="space-y-6 text-sm py-2">
          {/* Account Info Card */}
          <div className="bg-muted/40 p-4 rounded-lg space-y-3">
            {hasStatus && (
              <div className="flex justify-between items-center pb-2 border-b border-border">
                <span className="text-muted-foreground font-medium">
                  Status
                </span>
                <Badge className={getStatusColor(statusVal)}>
                  {capitalizeFirst(statusVal)}
                </Badge>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">
                Login ID
              </span>
              <span className="font-mono font-semibold">{account.Login}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Name</span>
              <span className="font-medium">{account.Name || '-'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Group</span>
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                {account.Group || '-'}
              </Badge>
            </div>
          </div>

          {/* Financial Details Card */}
          <div className="bg-muted/40 p-4 rounded-lg space-y-3">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Financial Details
            </h4>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Balance</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                $
                {(account.Balance || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Credit</span>
              <span className="font-semibold text-foreground">
                $
                {(account.Credit || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">
                Leverage
              </span>
              <span className="font-mono">
                {account.Leverage !== undefined && account.Leverage !== null
                  ? `1:${account.Leverage}`
                  : '-'}
              </span>
            </div>
          </div>

          {/* Contact & Regional Card */}
          {hasContactInfo && (
            <div className="bg-muted/40 p-4 rounded-lg space-y-3">
              <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Contact & Regional
              </h4>
              {hasEmail && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground font-medium">
                    Email
                  </span>
                  <span className="text-muted-foreground">{emailVal}</span>
                </div>
              )}
              {hasCountry && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground font-medium">
                    Country
                  </span>
                  <span className="font-medium">{countryVal}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </DialogComponent>
  );
}
